import { action, makeObservable, observable } from 'mobx'

import { IStringPair, LocationType } from '~/client/graph'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'

import FieldIds from '../../enums/DeliveryFieldIds'
import IHierarchyParent from '../../types/IHierarchyParent'
import ILocationDto from '../../types/ILocationDto'
import IMsDateInterval from '../../types/IMsDateInterval'
import LocationBase from './LocationBase'

type IIntegrationMetadata = {
  [key in KnownLocationIntegrationMetadataKeys]?: string
}

export enum LocationIntegrationType {
  MaturixStation = 'MaturixStation',
  None = 'None',
}

function getIntegrationType(integrationType: string) {
  switch (integrationType) {
    case LocationIntegrationType.MaturixStation:
      return LocationIntegrationType.MaturixStation
    default:
      return LocationIntegrationType.None
  }
}

export enum KnownLocationIntegrationMetadataKeys {
  Type = 'Type',
  MonitoringId = 'MonitoringId',
}

const knownLocationIntegrationMetadataKeys = Object.values(
  KnownLocationIntegrationMetadataKeys,
)

interface IIntegrationDto extends ILocationDto {
  metadata?: Array<IStringPair>
}

export default class LocationIntegration extends LocationBase<LocationIntegration> {
  public constructor(
    id: string,
    name: string,
    color: string,
    iconName: MapViewLocationIcon,
    projectId: string,
    code?: string,
    operatingIntervals?: IMsDateInterval[],
    parent?: IHierarchyParent,
    permittedCompanies?: string[],
    isDeleted?: boolean,
    assignedSitemaps?: string[],
    assignedGlobes?: string[],
  ) {
    super(
      id,
      name,
      color,
      iconName,
      projectId,
      code,
      operatingIntervals,
      parent,
      permittedCompanies,
      isDeleted,
      assignedSitemaps,
      assignedGlobes,
    )

    makeObservable(this)
  }

  public static fromDto(dto: IIntegrationDto) {
    const item = LocationBase.factory(
      LocationIntegration,
      dto,
      MapViewLocationIcon.Monitoring,
    )
    dto.metadata?.forEach(({ key, value }) => {
      if (key === KnownLocationIntegrationMetadataKeys.Type) {
        item.integrationType = getIntegrationType(value)
        if (item.integrationType === LocationIntegrationType.MaturixStation) {
          item.relatedFieldId = FieldIds.MATURIX_STATION
        }
      } else if (
        knownLocationIntegrationMetadataKeys.includes(
          key as KnownLocationIntegrationMetadataKeys,
        )
      ) {
        item.metadata[key] = value
      }
    })
    return item
  }

  public type = LocationType.Integration
  public relatedFieldId = FieldIds.INTEGRATION
  protected model = LocationIntegration

  @observable public metadata: IIntegrationMetadata = {}
  @observable public integrationType: LocationIntegrationType

  public is(type: LocationType | LocationIntegrationType): boolean {
    return this.integrationType === type || this.type === type
  }

  public isEqual(dto: LocationIntegration) {
    return (
      super.isEqual(dto) &&
      this.integrationType === dto.integrationType &&
      JSON.stringify(this.metadata) === JSON.stringify(dto.metadata)
    )
  }

  public copy(): LocationIntegration {
    const item = super.copy()
    item.metadata = JSON.parse(JSON.stringify(this.metadata))
    item.integrationType = this.integrationType

    return item
  }

  @action
  public getDto(): IIntegrationDto {
    const dto: IIntegrationDto = super.getDto()
    dto.metadata = []
    dto.metadata.push({
      key: KnownLocationIntegrationMetadataKeys.Type,
      value: this.integrationType,
    })
    Object.entries(this.metadata).forEach(([key, value]) => {
      dto.metadata.push({ key, value })
    })

    return dto
  }
}
